import React, { useState } from "react";
import { Box } from "@mui/material";
import { dt } from "../../utils/dt";
import { Machine } from "../../models/models";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { getMachineDisplayName } from "../../../../shared/utils/machineUtils";
import {
  ReadinessModel,
  ReadinessSummaryStrings,
  ReadinessSignalKind,
  readinessSummaryParts,
  readinessSummaryLabel,
} from "../../utils/midasReadiness";
import OemStar from "../OemStar";
import MidasReadinessPopup from "./MidasReadinessPopup";

// =====================================================================
//  MidasCardRow (DR-4) — ONE "Midas" label/value row appended under the
//  "System" row of a my-machinery card (host: MachineryCard vertical +
//  RedesignedMachineCard horizontal). Renders NOTHING when the machine has
//  no catalog link (manual machines show no Midas row — per artboard v2-3).
//
//  CONDITIONAL CLICKABILITY (Paper v2-3 "WHAT CHANGED"):
//   - CONNECTED (live) → plain static text "N docs · ● live", NOT clickable,
//     no popup (nothing to explain — it is already streaming).
//   - Every non-connected catalog-linked machine → clickable link (blue text
//     + › chevron) opening the shared MidasReadinessPopup. Prominent in the
//     "available" states (★ OEM available / Telemetry available / N docs · ★ OEM),
//     a subtle "View details ›" otherwise.
//
//  Labels/precedence come from the shared util (readinessSummaryLabel, used
//  for the accessible name); the styled composite is drawn from the same
//  derivation primitives so it matches every other readiness surface. NO
//  Ask-MIDAS / chat affordance. CONNECTED state is sourced by the caller from
//  the ONE batch /v1/gateway/machines/statuses fetch (fail-closed).
// =====================================================================

const clr = {
  docText: "#333333",
  blue: "#2E90E5",
  green: "#27AE60",
  muted: "#B9BEC6",
};

interface MidasCardRowProps {
  machine: Machine;
  /** Live-connected — sourced once per page from the batch status fetch. */
  connected?: boolean;
  /**
   * vertical  = MachineryCard (default card the user sees; 1/3 · 2/3 row).
   * horizontal = RedesignedMachineCard (full-width row appended under specs).
   */
  variant?: "vertical" | "horizontal";
}

const MidasCardRow: React.FC<MidasCardRowProps> = ({ machine, connected = false, variant = "vertical" }) => {
  const { translate } = useTranslation("myMachinery");
  const [popupOpen, setPopupOpen] = useState(false);

  const catalogModel = machine.catalogModel;
  // No catalog link → no Midas row at all (matches artboard + profile card).
  if (!catalogModel) return null;

  const readiness = catalogModel.readiness;
  const isOem = catalogModel.isOemPartner === true;
  const oemPartnerName = catalogModel.oemPartnerName;
  const manufacturerName = catalogModel.manufacturer?.name || machine.manufacturer?.name || null;

  const model: ReadinessModel = { readiness, isOemPartner: isOem, oemPartnerName };

  // Translated word bank → the shared util produces BOTH the accessible summary
  // AND the structured parts the styled composite renders, so the label and the
  // row can never drift out of precedence (item 10).
  const strings: ReadinessSummaryStrings = {
    doc: translate("summaryDoc"),
    docs: translate("summaryDocs"),
    oem: translate("summaryOem"),
    oemAvailable: translate("summaryOemAvailable"),
    telemetryAvailable: translate("summaryTelemetryAvailable"),
    live: translate("summaryLive"),
  };
  const parts = readinessSummaryParts(model, { connected }, strings);
  const ariaSummary = readinessSummaryLabel(model, { connected }, strings) || translate("viewDetails");

  const signalKind: ReadinessSignalKind = parts.signal.kind;
  const noDocs = parts.docPart === null;

  const clickable = !connected; // catalogModel already guaranteed above
  const showNoDocsYet = noDocs && (signalKind === "oem" || signalKind === "telemetry");

  const valueFontSize = variant === "vertical" ? "13px" : "12.5px";

  // --- value composite (icon + text + chevron) ---
  const chevron = (
    <Box component="span" sx={{ fontSize: valueFontSize, fontWeight: 600, lineHeight: 1, ml: "1px" }}>
      ›
    </Box>
  );

  const greenDot = (
    <Box sx={{ width: "6px", height: "6px", borderRadius: "50%", flexShrink: 0, backgroundColor: clr.green }} />
  );
  const hollowBlueDot = (
    <Box sx={{ width: "6px", height: "6px", borderRadius: "50%", flexShrink: 0, border: `1.3px solid ${clr.blue}`, boxSizing: "border-box" }} />
  );

  const renderSignal = () => {
    switch (signalKind) {
      case "live":
        return (
          <>
            {greenDot}
            <Box component="span" sx={{ fontSize: valueFontSize, fontWeight: 600, color: clr.green }}>
              {parts.signal.text}
            </Box>
          </>
        );
      case "oem":
        return (
          <>
            <OemStar size={11} />
            <Box component="span" className="midas-signal-text" sx={{ fontSize: valueFontSize, fontWeight: 600, color: clr.blue }}>
              {parts.signal.text}
            </Box>
            {chevron}
          </>
        );
      case "telemetry":
        return (
          <>
            {hollowBlueDot}
            <Box component="span" className="midas-signal-text" sx={{ fontSize: valueFontSize, fontWeight: 600, color: clr.blue }}>
              {parts.signal.text}
            </Box>
            {chevron}
          </>
        );
      default: // viewDetails (docs-only or empty) — visually quieter (item 18):
        // muted, non-bold text, no accent colour, so it reads as a subtle
        // informational affordance next to the loud "available" states.
        return (
          <>
            <Box component="span" className="midas-signal-text" sx={{ fontSize: valueFontSize, fontWeight: 500, color: clr.muted }}>
              {translate("viewDetails")}
            </Box>
            {chevron}
          </>
        );
    }
  };

  const valueInner = (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "1px", minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexWrap: "wrap" }}>
        {parts.docPart && (
          <Box component="span" sx={{ fontSize: valueFontSize, fontWeight: 600, color: clr.docText }}>
            {`${parts.docPart} ·`}
          </Box>
        )}
        {renderSignal()}
      </Box>
      {showNoDocsYet && (
        <Box component="span" sx={{ fontSize: "11.5px", color: clr.muted }}>
          {translate("midasNoDocsYet")}
        </Box>
      )}
    </Box>
  );

  // Clickable states use a real <button> for keyboard access; connected is a
  // plain, non-interactive block.
  const value = clickable ? (
    <Box
      component="button"
      type="button"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        setPopupOpen(true);
      }}
      aria-haspopup="dialog"
      aria-label={`${translate("midasRowLabel")}: ${ariaSummary}`}
      sx={{
        p: 0,
        m: 0,
        border: "none",
        background: "none",
        font: "inherit",
        textAlign: "left",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "flex-start",
        borderRadius: "4px",
        "&:hover .midas-signal-text": { textDecoration: "underline" },
        "&:focus-visible": { outline: `2px solid ${clr.blue}`, outlineOffset: "2px" },
      }}
    >
      {valueInner}
    </Box>
  ) : (
    <Box sx={{ display: "inline-flex", alignItems: "flex-start" }}>{valueInner}</Box>
  );

  // --- row shell (matches each host's sibling rows) ---
  const row =
    variant === "vertical" ? (
      <Box sx={{ display: "flex", mt: "8px", fontFamily: "inherit" }}>
        <Box
          sx={{
            width: "33.333%",
            flexShrink: 0,
            fontSize: "14px",
            fontWeight: 400,
            color: clr.docText,
          }}
        >
          {translate("midasRowLabel")}
        </Box>
        <Box sx={{ width: "66.667%", minWidth: 0 }}>{value}</Box>
      </Box>
    ) : (
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px", mt: "8px", minWidth: 0, fontFamily: dt.font }}>
        <Box sx={{ fontSize: "12px", fontFamily: dt.font, fontWeight: 400, color: dt.text.tertiary, whiteSpace: "nowrap", flexShrink: 0, pt: "1px" }}>
          {translate("midasRowLabel")}
        </Box>
        <Box sx={{ minWidth: 0, display: "flex", justifyContent: "flex-end" }}>{value}</Box>
      </Box>
    );

  return (
    <>
      {row}
      <MidasReadinessPopup
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        readiness={readiness}
        isOemPartner={isOem}
        oemPartnerName={oemPartnerName}
        liveStatus={connected ? "connected" : "disconnected"}
        machineOrModelName={getMachineDisplayName(machine)}
        manufacturerName={manufacturerName}
      />
    </>
  );
};

export default MidasCardRow;
